import type { IScheduledEmail, IScheduledEmailContentV2 } from '@/types/email.types';
import type { BrandliveEvent } from '@/types/projects.types';
import type { ThemePack } from '@/types/theme-packs.types';
import type { LanguagesAbbr, TranslateString } from '@/types/translation.types';
import { getEmailEditorFields } from '@/utils/scheduled-email-campaign';
import { resolveBlocksForLanguage, serializeBlocksForLanguage } from '@/utils/email-blocks';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { getDefaultBlocksForTemplate } from '@/micros/edit-project/grid-areas/main/campaigns/templates/default-blocks';
import { eventTitle as getEventTitle } from '@/micros/edit-project/grid-areas/main/campaigns/templates/utils/utils';
import type {
  EmailBlockContentV2,
  ResolvedEmailBlock,
} from '@/micros/edit-project/grid-areas/main/campaigns/templates/widgets/block-types';
import { initialFields } from './initial';
import type { CampaignEditorFields, CampaignEditorState } from './types';

// the template being edited - the working copy's, else the one the campaign was saved with
export const currentTemplateIdOf = (email: IScheduledEmail): string =>
  (email.working_customizations?.v2 as IScheduledEmailContentV2 | undefined)?.templateId ??
  (email.customizations.v2 as IScheduledEmailContentV2 | undefined)?.templateId ??
  'capri';

export const editorEventTitle = (event: BrandliveEvent | null, language: string | undefined) =>
  event ? getEventTitle(event, (language as LanguagesAbbr) ?? 'en') : undefined;

export function fieldsFromScheduledEmail(email: IScheduledEmail, language: string): CampaignEditorFields {
  const fields = getEmailEditorFields(email);

  const getLanguageText = (field: TranslateString) => {
    return (field?.[language] as string) || field?.base || '';
  };

  return {
    title: getLanguageText(fields.title),
    description: getLanguageText(fields.description),
    headingFont: fields.headingFont,
    bodyFont: fields.bodyFont,
    salutation: getLanguageText(fields.salutation),
    startIntro: getLanguageText(fields.startIntro),
    cta: getLanguageText(fields.cta),
    headingImage: fields.headingImage,
    mainImage: fields.mainImage,
    backgroundColor: fields.backgroundColor ?? '',
    containerColor: fields.containerColor ?? '',
    accentColor: fields.accentColor ?? '',
    headingTextColor: fields.headingTextColor ?? '',
    bodyTextColor: fields.bodyTextColor ?? '',
    secondaryAccentColor: fields.secondaryAccentColor ?? '',
    secondaryBackgroundColor: fields.secondaryBackgroundColor ?? '',
    buttonTextColor: fields.buttonTextColor ?? '',
    themeColorPackId: fields.themeColorPackId,
    hideBrandliveLogo: fields.hideBrandliveLogo ?? false,
    themeMode: fields.themeMode,
  };
}

const FIELD_KEYS = (Object.keys(initialFields) as (keyof CampaignEditorFields)[]).sort();

// a stable string for the whole editable set, so the sync can tell "the user has changed something"
// from "these are the values that were just loaded off the email". Serialized key by key rather than
// from the object itself, so the order the fields happened to be built in can't make two equal sets differ
export const editorContentSignature = (editor: Pick<CampaignEditorState, 'fields' | 'blocks'>) =>
  JSON.stringify([FIELD_KEYS.map((key) => editor.fields[key] ?? null), editor.blocks]);

// the stored blocks when there are any, otherwise the template's defaults
export function editorBlocksFor(
  storedBlocks: EmailBlockContentV2[] | undefined,
  templateId: string,
  eventTitle: string | undefined,
  language: string | undefined,
  baseLanguage: string
): ResolvedEmailBlock[] | null {
  const blocks = storedBlocks?.length ? storedBlocks : getDefaultBlocksForTemplate(templateId, eventTitle);
  return blocks ? resolveBlocksForLanguage(blocks, language, baseLanguage) : null;
}

// the editor's fields written back over the stored content, translated into the language being edited
export function buildCustomizationsPatch(
  editor: Pick<CampaignEditorState, 'fields' | 'blocks' | 'language' | 'baseLanguage'>,
  content: IScheduledEmailContentV2,
  theme: ThemePack | undefined
) {
  const { fields, blocks, baseLanguage } = editor;
  const language = editor.language ?? undefined;
  const translate = (translateString: IScheduledEmailContentV2['title'], input: string) =>
    updateTranslateKey({ translateString, input, baseLanguage, language: language ?? null });

  return {
    title: translate(content.title, fields.title),
    description: translate(content.description, fields.description),
    headingFont: fields.headingFont,
    bodyFont: fields.bodyFont,
    salutation: translate(content.salutation, fields.salutation),
    startIntro: translate(content.startIntro, fields.startIntro),
    cta: translate(content.cta, fields.cta),
    mainImage: fields.mainImage,
    headingImage: fields.headingImage,
    backgroundColor: fields.backgroundColor,
    containerColor: fields.containerColor,
    secondaryAccentColor: fields.secondaryAccentColor,
    secondaryBackgroundColor: fields.secondaryBackgroundColor,
    accentColor: fields.accentColor,
    headingTextColor: fields.headingTextColor,
    bodyTextColor: fields.bodyTextColor,
    buttonTextColor: fields.buttonTextColor,
    // null clears a removed theme - undefined would leave the previous one saved
    themeColorPackId: fields.themeColorPackId ?? null,
    // saved whole alongside its id, so the senders render with the same pack without looking it up.
    // While the packs are still loading theme is undefined, which leaves the saved pack as it is
    themeColorPack: fields.themeColorPackId === undefined ? null : theme,
    themeMode: fields.themeMode,
    hideBrandliveLogo: fields.hideBrandliveLogo,
    blocks: blocks ? serializeBlocksForLanguage(blocks, content.blocks, language, baseLanguage) : undefined,
  };
}

// replaces one block, by id, in the editor's blocks
export const updateBlock = (
  state: CampaignEditorState,
  blockId: string,
  update: (block: ResolvedEmailBlock) => ResolvedEmailBlock
) => {
  if (!state.blocks) return;
  state.blocks = state.blocks.map((block) => (block.id === blockId ? update(block) : block));
};
